import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useNotificationSound } from './hooks/useNotificationSound';
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

// Layouts
import AdminLayout from './components/layouts/AdminLayout';
import ContractorLayout from './components/layouts/ContractorLayout';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import MagicLinkPage from './pages/auth/MagicLinkPage';
import VerifyPage from './pages/auth/VerifyPage';
import WelcomePage from './pages/auth/WelcomePage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Public pages
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Support from './pages/Support';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminSchedule from './pages/admin/Schedule';
import AdminScheduler from './pages/admin/Scheduler';
import AdminGigEdit from './pages/admin/GigEdit';
import AdminClients from './pages/admin/Clients';
import AdminContractors from './pages/admin/Contractors';
import AdminUsers from './pages/admin/AdminUsers';
import AdminReports from './pages/admin/Reports';
import AdminTemplates from './pages/admin/Templates';
import ManageRoles from './pages/admin/ManageRoles';

// Events Module pages
import EventsList from './pages/admin/EventsList';
import EventBuilder from './pages/admin/EventBuilder';
import EventCategories from './pages/admin/EventCategories';
import VendorsList from './pages/admin/VendorsList';
import VendorDiscovery from './pages/admin/VendorDiscovery';

// Accountant pages
import AccountantExports from './pages/accountant/Exports';

// Shared pages
import Chat from './pages/shared/Chat';
import ContractorDirectory from './pages/shared/ContractorDirectory';

// Admin pages (additional)
import WorkDirectory from './pages/admin/WorkDirectory';

// Contractor pages
import ContractorDashboard from './pages/contractor/Dashboard';
import ContractorCalendar from './pages/contractor/Calendar';
import ContractorAvailability from './pages/contractor/Availability';
import ContractorEarnings from './pages/contractor/Earnings';
import ContractorGigDetail from './pages/contractor/GigDetail';
import Profile from './pages/contractor/Profile';

// Loading spinner
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-500">Loading...</p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === 'CONTRACTOR') {
      return <Navigate to="/my/dashboard" replace />;
    }
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
}

// Push notification deep link handler
function PushNotificationHandler() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Only set up listener on native platforms
    if (!Capacitor.isNativePlatform()) return;

    // Handle notification tap (when app opens from notification)
    const setupPushListeners = async () => {
      // When user taps on a notification
      await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('[PUSH] Notification tapped:', notification);
        
        const data = notification.notification.data;
        if (!data) return;

        // Wait a moment for auth to be ready
        setTimeout(() => {
          // Determine route based on notification type and user role
          const isContractor = user?.role === 'CONTRACTOR';
          const basePath = isContractor ? '/my' : '/admin';

          switch (data.type) {
            case 'chat':
              // Navigate to chat with specific room
              if (data.roomId) {
                navigate(`${basePath}/chat?room=${data.roomId}`);
              } else {
                navigate(`${basePath}/chat`);
              }
              break;

            case 'gig':
              // Contractors go to gig detail, admins go to gig edit
              if (isContractor && data.gigId) {
                navigate(`/my/gig/${data.gigId}`);
              } else if (data.gigId) {
                navigate(`/admin/gigs/${data.gigId}`);
              } else {
                navigate(`${basePath}/calendar`);
              }
              break;

            case 'payment':
              // Contractors go to earnings
              if (isContractor) {
                navigate('/my/earnings');
              } else {
                navigate('/admin/reports');
              }
              break;

            default:
              // Default to dashboard
              navigate(`${basePath}/dashboard`);
          }
        }, 100);
      });

      // Handle notification received while app is in foreground (optional)
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[PUSH] Notification received in foreground:', notification);
        // Could show an in-app toast/banner here
      });
    };

    setupPushListeners();

    // Cleanup listeners on unmount
    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [navigate, user]);

  return null; // This component doesn't render anything
}

// App routes
function AppRoutes() {
  const { user, loading } = useAuth();
  
  // Initialize notification sound listener
  useNotificationSound();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      {/* Push notification handler - only active when user is logged in */}
      {user && <PushNotificationHandler />}
      
      <Routes>
        {/* Public routes */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/support" element={<Support />} />
        <Route path="/login" element={
          user ? <Navigate to={user.role === 'CONTRACTOR' ? '/my/dashboard' : '/admin/dashboard'} replace /> : <LoginPage />
        } />
        <Route path="/magic-link" element={<MagicLinkPage />} />
        <Route path="/auth/verify" element={<VerifyPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Welcome/Onboarding - requires auth but no role check */}
        <Route path="/welcome" element={
          user ? <WelcomePage /> : <Navigate to="/login" replace />
        } />

        {/* Admin/Accountant routes */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'ACCOUNTANT']}>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="schedule" element={<AdminSchedule />} />
          <Route path="scheduler" element={<AdminScheduler />} />
          <Route path="gigs/new" element={<AdminGigEdit />} />
          <Route path="gigs/:id" element={<AdminGigEdit />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="contractors" element={<AdminContractors />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="templates" element={<AdminTemplates />} />
          <Route path="roles" element={<ManageRoles />} />
          <Route path="exports" element={<AccountantExports />} />
          <Route path="chat" element={<Chat />} />
          <Route path="directory" element={<WorkDirectory />} />
          <Route path="directory/contractors" element={<ContractorDirectory />} />
          <Route path="profile" element={<Profile />} />
          <Route path="event-categories" element={<EventCategories />} />
          
          {/* Events Module Routes */}
          <Route path="events" element={<EventsList />} />
          <Route path="events/brainstorm" element={<EventBuilder />} />
          <Route path="events/builder" element={<EventBuilder />} />
          <Route path="events/builder/:eventId" element={<EventBuilder />} />
          <Route path="events/quotes" element={<EventsList />} />
          
          {/* Vendor Hub Routes */}
          <Route path="vendors" element={<VendorsList />} />
          <Route path="vendors/discovery" element={<VendorDiscovery />} />
          <Route path="vendors/preferred" element={<VendorsList />} />
          <Route path="vendors/search" element={<VendorsList />} />
        </Route>

        {/* Contractor routes */}
        <Route path="/my" element={
          <ProtectedRoute allowedRoles={['CONTRACTOR']}>
            <ContractorLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<ContractorDashboard />} />
          <Route path="calendar" element={<ContractorCalendar />} />
          <Route path="availability" element={<ContractorAvailability />} />
          <Route path="earnings" element={<ContractorEarnings />} />
          <Route path="chat" element={<Chat />} />
          <Route path="directory" element={<ContractorDirectory />} />
          <Route path="gig/:id" element={<ContractorGigDetail />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Default redirect */}
        <Route path="/" element={
          user ? (
            <Navigate to={user.role === 'CONTRACTOR' ? '/my/dashboard' : '/admin/dashboard'} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        } />

        {/* 404 */}
        <Route path="*" element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">404</h1>
              <p className="text-gray-500 mb-4">Page not found</p>
              <a href="/" className="btn-primary">Go Home</a>
            </div>
          </div>
        } />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
} 
